import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Box,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Alert } from '@material-ui/lab';
import { InfoCard, LinkButton, Progress } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ConfigEditorClient } from 'config-editor';
import {
  resolveEntityConfigPlan,
  summarizeConfig,
  type ConfigSummary,
  type ConfigTargetType,
  type LambdaSummary,
} from 'config-editor-common';

// Human label per config target type. Typed on ConfigTargetType (not Record<string, string>) so
// adding a new target type is a compile error here until a label is supplied.
const TYPE_LABELS: Record<ConfigTargetType, string> = {
  api: 'API',
  'step-function': 'Step Function',
  lambda: 'Lambda',
};

// Tick for true, dash for an explicit false, '?' for unknown/unresolved (undefined) so a value we
// couldn't resolve is not shown identically to a real false.
const boolText = (value?: boolean) => {
  if (value === undefined) return '?';
  return value ? '✔' : '–';
};

/**
 * "inherited" chip shown next to a value that came from group `defaults` / the Terraform module
 * default rather than the lambda itself (mirrors terraform-phx-api / terraform-phxp-step-function).
 */
const InheritedBadge = () => (
  <Chip size="small" variant="outlined" label="inherited" style={{ marginLeft: 6, height: 18 }} />
);

const FieldCell = ({
  value,
  inherited,
  align,
}: {
  value: ReactNode;
  inherited?: boolean;
  align?: 'right' | 'center' | 'left';
}) => (
  <TableCell align={align}>
    {value}
    {inherited && <InheritedBadge />}
  </TableCell>
);

const LambdaTable = ({ lambdas }: { lambdas: LambdaSummary[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Lambda</TableCell>
        <TableCell>Group</TableCell>
        <TableCell>Runtime</TableCell>
        <TableCell align="right">Mem&nbsp;(MB)</TableCell>
        <TableCell align="right">Timeout&nbsp;(s)</TableCell>
        <TableCell align="center">SnapStart</TableCell>
        <TableCell align="center">Snap&nbsp;alias</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {lambdas.map(l => (
        <TableRow key={`${l.group}/${l.name}`}>
          <TableCell>
            {l.name}
            {l.capacityProvider && (
              <Chip
                size="small"
                label="LMI"
                style={{ marginLeft: 8 }}
                title={`capacity_provider: ${l.capacityProvider}`}
              />
            )}
          </TableCell>
          <TableCell>{l.group}</TableCell>
          <FieldCell value={l.runtime ?? '–'} inherited={l.inherited.runtime} />
          <FieldCell align="right" value={l.memorySize ?? '–'} inherited={l.inherited.memorySize} />
          <FieldCell align="right" value={l.timeout ?? '–'} inherited={l.inherited.timeout} />
          <FieldCell
            align="center"
            value={boolText(l.snapstart)}
            inherited={l.inherited.snapstart}
          />
          <FieldCell
            align="center"
            value={boolText(l.snapAlias)}
            inherited={l.inherited.snapAlias}
          />
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/** A single field bullet, with an "inherited" badge when the value isn't set on the lambda. */
const DetailItem = ({
  label,
  value,
  inherited,
}: {
  label: string;
  value: ReactNode;
  inherited?: boolean;
}) => (
  <Typography component="li" variant="body2" style={{ marginBottom: 4 }}>
    <strong>{label}:</strong> {value}
    {inherited && <InheritedBadge />}
  </Typography>
);

/** Bullet-list view of one lambda's resolved fields — used on a single-lambda entity page. */
const LambdaDetailList = ({ lambda }: { lambda: LambdaSummary }) => (
  <Box component="ul" mt={0} mb={0} pl={2}>
    {lambda.handler && <DetailItem label="Handler" value={lambda.handler} />}
    <DetailItem label="Group" value={lambda.group} />
    <DetailItem
      label="Runtime"
      value={lambda.runtime ?? '–'}
      inherited={lambda.inherited.runtime}
    />
    <DetailItem
      label="Memory (MB)"
      value={lambda.memorySize ?? '–'}
      inherited={lambda.inherited.memorySize}
    />
    <DetailItem
      label="Timeout (s)"
      value={lambda.timeout ?? '–'}
      inherited={lambda.inherited.timeout}
    />
    <DetailItem
      label="SnapStart"
      value={boolText(lambda.snapstart)}
      inherited={lambda.inherited.snapstart}
    />
    <DetailItem
      label="Snap alias"
      value={boolText(lambda.snapAlias)}
      inherited={lambda.inherited.snapAlias}
    />
    {lambda.capacityProvider && (
      <DetailItem label="Capacity provider (LMI)" value={lambda.capacityProvider} />
    )}
  </Box>
);

/**
 * Overview card that previews an entity's config.json and deep-links to the Config Editor.
 * Works for three entity shapes (see resolveEntityConfigPlan):
 *  - API / Step Function entities -> full lambda table (+ step-function state count).
 *  - Lambda entities -> just that lambda's resolved fields, with "inherited" badges when a
 *    value comes from group defaults / the Terraform module default rather than the lambda.
 * Renders nothing for entities not backed by a known config repo.
 */
export const ConfigEditorLink = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const plan = useMemo(() => resolveEntityConfigPlan(entity), [entity]);

  const [summary, setSummary] = useState<ConfigSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!plan) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSummary(null);

    const load = async () => {
      const path = `${plan.basePath}/${plan.item}`;
      const client = new ConfigEditorClient({ discoveryApi, fetchApi });

      const { config } = await client.fetchConfig(plan.owner, plan.repo, path, plan.branch);

      let definition: string | undefined;
      if (plan.type === 'step-function') {
        try {
          const def = await client.fetchDefinition(
            plan.owner,
            plan.repo,
            `${path}/definition.json`,
            plan.branch,
          );
          definition = def.content;
        } catch {
          // Definition is optional for the summary; lambdas still render without it.
        }
      }

      return summarizeConfig({
        type: plan.type,
        config,
        // template_name is canonical; fall back to the legacy `template` key so a not-yet-migrated
        // non-phx config (e.g. condor-api-v1) isn't misread as phx and shown with phx defaults.
        templateName: config?.template_name ?? config?.template,
        definition,
      });
    };

    load()
      .then(result => {
        if (!cancelled) setSummary(result);
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [plan, discoveryApi, fetchApi]);

  if (!plan) return null;

  const label = TYPE_LABELS[plan.type];
  const params = new URLSearchParams({
    type: plan.type,
    component: plan.component,
    item: plan.item,
    branch: plan.branch,
  });
  const to = `/config-editor?${params.toString()}`;

  // For a lambda entity, show only that lambda's row.
  const lambdas =
    summary && plan.focusLambda
      ? summary.lambdas.filter(l => l.name === plan.focusLambda)
      : summary?.lambdas ?? [];

  return (
    <InfoCard title="Configuration">
      <Typography variant="body2" color="textSecondary" paragraph>
        {plan.focusLambda ? (
          <>
            Lambda <strong>{plan.focusLambda}</strong> in API <strong>{plan.item}</strong>. Edit its
            config in the Config Editor (opens on <strong>{plan.branch}</strong>).
          </>
        ) : (
          <>
            Edit this {label}'s configuration in the Config Editor. Opens pre-loaded on the{' '}
            <strong>{plan.branch}</strong> branch with this item's config.
          </>
        )}
      </Typography>

      {loading && <Progress />}

      {error && !loading && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          Config preview unavailable on <strong>{plan.branch}</strong>: {error}
        </Alert>
      )}

      {summary && !loading && (
        <Box mb={2}>
          {summary.templateName && (
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Template: {summary.templateName}
            </Typography>
          )}

          {plan.type === 'step-function' && !plan.focusLambda && (
            <Typography variant="body2" gutterBottom>
              States: <strong>{summary.stateCount ?? 'unknown'}</strong>
              {summary.iamPolicyNames && summary.iamPolicyNames.length > 0 && (
                <>
                  {' '}
                  · IAM policies: <strong>{summary.iamPolicyNames.length}</strong>
                </>
              )}
            </Typography>
          )}

          {(() => {
            if (lambdas.length === 0) {
              return (
                <Typography variant="body2" color="textSecondary">
                  {plan.focusLambda
                    ? `Lambda "${plan.focusLambda}" not found in this API's config.`
                    : 'No lambdas found in this config.'}
                </Typography>
              );
            }
            if (plan.focusLambda) {
              return <LambdaDetailList lambda={lambdas[0]} />;
            }
            return <LambdaTable lambdas={lambdas} />;
          })()}
        </Box>
      )}

      <LinkButton
        to={to}
        variant="contained"
        color="primary"
        startIcon={<EditIcon />}
        data-ai-anchor="open-config-editor"
      >
        Open in Config Editor
      </LinkButton>
    </InfoCard>
  );
};
